import type { ComponentProps } from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";

import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Icon-only button that always names itself: `label` is both the
 * accessible name and the tooltip, with an optional shortcut key. Buttons
 * that open a menu or popover don't need it (the popup explains itself).
 */
export function ToolIconButton({
  icon,
  label,
  shortcut,
  size = "icon-sm",
  variant = "ghost",
  iconClassName,
  loading = false,
  ...props
}: Omit<ComponentProps<typeof Button>, "children" | "aria-label"> & {
  icon: IconSvgElement;
  label: string;
  shortcut?: string;
  iconClassName?: string;
  /** The action is running: a disabled button with a spinner in place of the icon. */
  loading?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button variant={variant} size={size} aria-label={label} loading={loading} {...props} />
        }
      >
        <HugeiconsIcon icon={icon} className={iconClassName} aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>
        {label}
        {shortcut && <Kbd>{shortcut}</Kbd>}
      </TooltipContent>
    </Tooltip>
  );
}
